import { useEffect, useState } from 'react';
import {
  FileText,
  Loader2,
  Paperclip,
  Pencil,
  SmilePlus,
  Trash2,
  Video,
} from 'lucide-react';

import { chatImageUrl } from '../../services/teamsDmApi';
import { sanitizeHtml } from '../../utils/sanitize';

// Teams' reactions that Graph takes by name, and how they look.
export const REACTIONS = {
  like: '👍',
  heart: '❤️',
  laugh: '😆',
  surprised: '😮',
  sad: '😢',
  angry: '😡',
};

export const when = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const clock = (iso) =>
  new Date(iso).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * A Teams system event, shown as Teams does: a small centred line, or for a
 * finished call a card with its time, recording and transcript. The recap
 * opens Oqlous' meeting summary when we processed that meeting, else Teams.
 */
export const SystemEvent = ({ message, title, teamsUrl, onOpenSummary }) => {
  if (message.system !== 'meeting') {
    return (
      <li className="py-0.5 text-center text-[11px] text-zinc-500">
        {message.text}
        <span className="ml-1.5 text-zinc-600">{when(message.sent_at)}</span>
      </li>
    );
  }
  const recapUrl = message.transcript_url || teamsUrl;
  const range = message.started_at
    ? `${when(message.started_at)} – ${clock(message.sent_at)}`
    : when(message.sent_at);
  return (
    <li className="flex flex-col items-center gap-1 py-1">
      <span className="text-[11px] text-zinc-500">{message.text}</span>
      <div className="w-full max-w-[85%] rounded-xl border border-zinc-700 bg-zinc-900 p-2.5">
        <p className="truncate text-[13px] font-medium text-zinc-100">
          {title || 'Meeting'}
        </p>
        <p className="text-[11px] text-zinc-400">{range}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {message.oqlous_recap === 'preparing' && (
            <span className="flex items-center gap-1 rounded-lg border border-zinc-700 px-2.5 py-1 text-[12px] text-zinc-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Recap being prepared
            </span>
          )}
          {message.oqlous_event_id && message.oqlous_recap !== 'preparing' ? (
            <button
              type="button"
              onClick={() => onOpenSummary(message.oqlous_event_id)}
              className="flex items-center gap-1 rounded-lg bg-[#5652CF] px-2.5 py-1 text-[12px] font-semibold text-white hover:brightness-110"
            >
              <FileText className="h-3.5 w-3.5" />
              View recap
            </button>
          ) : (
            message.transcript &&
            recapUrl && (
              <a
                href={recapUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1 rounded-lg border border-zinc-700 px-2.5 py-1 text-[12px] text-zinc-200 hover:bg-zinc-800"
              >
                <FileText className="h-3.5 w-3.5" />
                View recap in Teams
              </a>
            )
          )}
          {message.recording_url && (
            <a
              href={message.recording_url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 text-[12px] text-indigo-300 hover:underline"
            >
              <Video className="h-3.5 w-3.5" />
              Recording
            </a>
          )}
        </div>
        {message.transcript && (
          <p className="mt-1.5 text-[11px] text-zinc-500">
            Content: Transcript
          </p>
        )}
      </div>
    </li>
  );
};

/** An inline Teams image, fetched through our API with the member's token. */
const ChatImage = ({ accessToken, chatId, messageId, hostedId }) => {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url;
    let current = true;
    chatImageUrl(accessToken, chatId, messageId, hostedId)
      .then((objectUrl) => {
        url = objectUrl;
        if (current) setSrc(objectUrl);
      })
      .catch(() => current && setFailed(true));
    return () => {
      current = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [accessToken, chatId, messageId, hostedId]);
  if (failed)
    return <span className="text-[11px] text-zinc-500">Image unavailable</span>;
  if (!src)
    return (
      <span className="block h-24 w-32 animate-pulse rounded bg-zinc-700/50" />
    );
  return (
    <img
      src={src}
      alt="Shared in Teams"
      className="max-h-60 max-w-full rounded-lg"
    />
  );
};

/**
 * One message: formatted text (sanitised), images, files and reactions.
 * `onReact(reaction, on)` is given for chats (channels show reactions only).
 */
const MessageBubble = ({
  accessToken,
  chatId,
  message,
  onReact,
  onEdit,
  onDelete,
}) => {
  const [picking, setPicking] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const mine = message.from_me;
  // Only the member's own messages, and only real ones (not just sent locally).
  const own = mine && onEdit && !String(message.id).startsWith('sent-');
  // Editing replaces the whole text; images and files would be lost.
  const editable = own && !message.images?.length && !message.files?.length;

  if (editing) {
    const value = draft.trim();
    const save = async () => {
      if (!value || value === message.text) return setEditing(false);
      if (await onEdit(value)) setEditing(false);
      return undefined;
    };
    return (
      <li className="flex flex-col items-end">
        <textarea
          aria-label="Edit message"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              save();
            }
            if (e.key === 'Escape') setEditing(false);
          }}
          rows={2}
          className="w-[85%] rounded-lg border border-[#5652CF] bg-zinc-900 p-2 text-[13px] text-zinc-100 outline-none"
        />
        <span className="mt-1 flex gap-2 text-[11px]">
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="text-zinc-400 hover:text-zinc-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!value}
            className="font-semibold text-indigo-300 hover:underline disabled:opacity-40"
          >
            Save
          </button>
        </span>
      </li>
    );
  }

  return (
    <li className={`group flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
      <span className="mb-0.5 text-[10px] text-zinc-500">
        {mine ? 'You' : message.from} · {when(message.sent_at)}
        {message.edited ? ' · edited' : ''}
      </span>
      <div
        className={`max-w-[85%] break-words rounded-xl px-3 py-1.5 text-[13px] ${
          mine ? 'bg-[#5652CF]/30 text-zinc-100' : 'bg-zinc-800 text-zinc-100'
        }`}
      >
        {message.html ? (
          <div
            className="teams-html space-y-1 [&_a]:text-indigo-300 [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-4 [&_ul]:list-disc [&_ul]:pl-4"
            // Graph's message HTML, cleaned by DOMPurify first.
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(message.html) }}
          />
        ) : (
          message.text && (
            <span className="whitespace-pre-wrap">{message.text}</span>
          )
        )}
        {message.images?.length > 0 && chatId && (
          <div className="mt-1 flex flex-col gap-1">
            {message.images.map((hostedId) => (
              <ChatImage
                key={hostedId}
                accessToken={accessToken}
                chatId={chatId}
                messageId={message.id}
                hostedId={hostedId}
              />
            ))}
          </div>
        )}
        {message.files?.map((file) => (
          <a
            key={file.url}
            href={file.url}
            target="_blank"
            rel="noreferrer"
            className="mt-1 flex items-center gap-1 text-[12px] text-indigo-300 underline"
          >
            <Paperclip className="h-3 w-3 shrink-0" />
            <span className="truncate">{file.name}</span>
          </a>
        ))}
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-1">
        {own && (
          <span className="flex gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
            {editable && (
              <button
                type="button"
                aria-label="Edit message"
                title="Edit"
                onClick={() => {
                  setDraft(message.text || '');
                  setEditing(true);
                }}
                className="rounded p-0.5 text-zinc-500 hover:text-zinc-200"
              >
                <Pencil className="h-3 w-3" />
              </button>
            )}
            <button
              type="button"
              aria-label="Delete message"
              title="Delete"
              onClick={onDelete}
              className="rounded p-0.5 text-zinc-500 hover:text-red-400"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </span>
        )}
        {(message.reactions || []).map((r) => (
          <button
            key={r.type}
            type="button"
            disabled={!onReact || !REACTIONS[r.type]}
            onClick={() => onReact?.(r.type, !r.mine)}
            aria-pressed={r.mine}
            title={r.mine ? 'Remove your reaction' : 'React'}
            className={`rounded-full border px-1.5 text-[11px] ${
              r.mine
                ? 'border-[#5652CF] bg-[#5652CF]/20 text-zinc-100'
                : 'border-zinc-700 text-zinc-300'
            }`}
          >
            {REACTIONS[r.type] || r.type} {r.count}
          </button>
        ))}
        {onReact && (
          <span className="relative">
            <button
              type="button"
              aria-label="Add reaction"
              onClick={() => setPicking((p) => !p)}
              className="rounded-full p-0.5 text-zinc-500 opacity-0 transition-opacity hover:text-zinc-200 focus:opacity-100 group-hover:opacity-100"
            >
              <SmilePlus className="h-3.5 w-3.5" />
            </button>
            {picking && (
              <span
                className={`absolute bottom-full z-10 mb-1 flex gap-0.5 whitespace-nowrap rounded-full border border-zinc-700 bg-zinc-900 px-1 py-0.5 shadow-lg ${mine ? 'right-0' : 'left-0'}`}
              >
                {Object.entries(REACTIONS).map(([type, emoji]) => (
                  <button
                    key={type}
                    type="button"
                    aria-label={`React ${type}`}
                    onClick={() => {
                      setPicking(false);
                      const mine = (message.reactions || []).find(
                        (r) => r.type === type,
                      )?.mine;
                      onReact(type, !mine);
                    }}
                    className="rounded-full px-1 text-[14px] hover:bg-zinc-800"
                  >
                    {emoji}
                  </button>
                ))}
              </span>
            )}
          </span>
        )}
      </div>
    </li>
  );
};

export default MessageBubble;
